import styled, { css } from 'styled-components';

export const SETTINGS_FIXED_HEIGHT = 560;
export const CROP_SIZE = 240;

/* ---- Layout ---- */

export const LayoutRow = styled.div`
  display: flex;
  height: ${SETTINGS_FIXED_HEIGHT}px;
  min-height: ${SETTINGS_FIXED_HEIGHT}px;
  margin: -8px -4px -4px;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    height: auto;
    min-height: auto;
    overflow: visible;
  }
`;

export const SideNav = styled.nav`
  width: 190px;
  flex-shrink: 0;
  padding: 8px 0 8px 0;
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    padding: 0 0 0 8px;
  }
`;

export const NavItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px;
  margin: 0 8px;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.accentLight};
      color: ${theme.colors.textPrimary};
      font-weight: 600;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: 0;
    padding: 10px 14px;
    border-radius: 0;
    border-bottom: 2px solid transparent;
    ${({ $active, theme }) =>
      $active &&
      css`
        background: transparent;
        border-bottom-color: ${theme.colors.accent};
      `}
  }
`;

export const ScrollFadeWrap = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const ScrollFadeMask = styled.div<{
  $position: 'top' | 'bottom';
  $visible: boolean;
}>`
  position: absolute;
  left: 0;
  right: 14px;
  height: 36px;
  pointer-events: none;
  z-index: 2;
  transition: opacity 0.25s ease;

  ${({ $position }) => ($position === 'top' ? 'top: 0;' : 'bottom: 0;')}

  opacity: ${({ $visible, $position }) =>
    $position === 'bottom' ? 0 : $visible ? 1 : 0};

  background: linear-gradient(
    ${({ $position }) => ($position === 'top' ? 'to bottom' : 'to top')},
    ${({ theme }) => theme.colors.bgPrimary},
    transparent
  );
`;

/*
 * ContentArea는 네이티브 스크롤바를 완전히 숨기고, 별도의 커스텀 트랙/썸을
 * 오버레이로 그려 autohide 동작(일부 Chromium/Electron 환경)을 우회한다.
 * 실제 스크롤은 네이티브 overflow로 처리되므로 휠/터치/키보드 동작은 그대로 동작한다.
 */
export const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  /* right padding은 커스텀 스크롤바(≈6px @ right:8px)와 충분한 간격을 두기 위해 넉넉히 확보 */
  padding: 8px 28px 4px 24px;
  overflow-y: auto;

  /* Firefox */
  scrollbar-width: none;
  /* Edge/IE */
  -ms-overflow-style: none;
  /* WebKit */
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 16px 0 0;
    max-height: none;
  }
`;

/* ---- Custom (JS-driven) scrollbar ---- */

export const CustomScrollTrack = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 10px;
  bottom: 10px;
  right: 8px;
  width: 6px;
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease, width 0.15s ease;
  z-index: 3;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  touch-action: none;

  &:hover {
    width: 8px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const CustomScrollThumb = styled.div<{ $dragging: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 999px;
  background: ${({ theme, $dragging }) => {
    const isDark = theme.mode === 'dark';
    if ($dragging) return isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
    return isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)';
  }};
  cursor: grab;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)'};
  }

  &:active {
    cursor: grabbing;
  }
`;

/* ---- Section ---- */

export const SectionBlock = styled.div`
  margin-bottom: 28px;
  &:last-child { margin-bottom: 0; }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

export const SectionTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

export const SectionDesc = styled.p`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 2px 0 8px;
  line-height: 1.4;
`;

export const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: 10px 0 16px;
`;

/* ---- Fields ---- */

export const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:last-of-type {
    border-bottom: none;
  }
`;

export const FieldLabel = styled.span`
  width: 72px;
  flex-shrink: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const FieldValue = styled.span`
  flex: 1;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-height: 20px;
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

/* ---- Profile picture ---- */

export const ProfilePicRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

export const PicActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const PicActionBtn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 999px;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme, $danger }) => ($danger ? theme.colors.error : theme.colors.textSecondary)};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:hover {
    background: ${({ theme, $danger }) =>
      $danger ? `${theme.colors.error}10` : theme.colors.bgHover};
    border-color: ${({ theme, $danger }) =>
      $danger ? `${theme.colors.error}40` : theme.colors.border};
    color: ${({ theme, $danger }) => ($danger ? theme.colors.error : theme.colors.textPrimary)};
  }

  svg { flex-shrink: 0; }
`;

export const AvatarLarge = styled.div<{ $bg: string }>`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 22%;
  overflow: hidden;
  flex-shrink: 0;
  background: ${({ $bg }) => $bg};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.4)'
      : '0 2px 8px rgba(0,0,0,0.1)'};
  color: #ffffff;
`;

export const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #ffffff;
`;

export const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: inherit;
  font-size: 1.5rem;
  font-weight: 700;
`;

export const AvatarOverlay = styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: white;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
  border: none;

  ${AvatarLarge}:hover & {
    opacity: 1;
  }
`;

/* ---- Image crop modal ---- */

export const CropOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.3)'};
`;

export const CropDialog = styled.div`
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  border-radius: 18px;
  padding: 24px;
  width: 340px;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 24px 60px rgba(0,0,0,0.4), 0 8px 20px rgba(0,0,0,0.25)'
      : '0 24px 60px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.08)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

export const CropTitle = styled.h3`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const CropViewport = styled.div`
  position: relative;
  width: ${CROP_SIZE}px;
  height: ${CROP_SIZE}px;
  border-radius: 50%;
  overflow: hidden;
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

export const CropCanvas = styled.div`
  position: absolute;
  inset: 0;
  background: #ffffff;
`;

export const CropImg = styled.img`
  position: absolute;
  pointer-events: none;
`;

export const CropCircle = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.accent};
  pointer-events: none;
  z-index: 2;
`;

export const CropDimmer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
`;

export const CropZoomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

export const ZoomLabel = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

export const ZoomSlider = styled.input`
  flex: 1;
  height: 3px;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
  border-radius: 2px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    border: none;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2), 0 0 0 0.5px rgba(0,0,0,0.08);
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    border: none;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2), 0 0 0 0.5px rgba(0,0,0,0.08);
  }
`;

export const CropActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  width: 100%;
`;

/* ---- Delete confirm ---- */

export const DeleteConfirmBox = styled.div`
  background: ${({ theme }) => theme.colors.errorBg};
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 14px;
`;

export const ErrorMsg = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.error};
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.error}10;
  border-radius: 6px;
  margin-bottom: 8px;
  line-height: 1.5;
`;

export const DeleteMsg = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.error};
  margin-bottom: 12px;
  line-height: 1.5;
`;

/* ---- Subscription ---- */

export const PlanBox = styled.div`
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 16px;
`;

export const PlanNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

export const PlanName = styled.span`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const PlanPrice = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 12px;
`;

export const PlanMeta = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
  line-height: 1.6;
`;

export const PlanFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PlanFeature = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const BillingMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin: 0 0 10px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const BillingMetaItem = styled.div`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const BillingMetaLabel = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const BillingMetaValue = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 600;
`;

export const InlineInfo = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 8px 0;
`;

export const InlineWarning = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.warning};
  background: ${({ theme }) => `${theme.colors.warning}14`};
  border: 1px solid ${({ theme }) => `${theme.colors.warning}30`};
  border-radius: 8px;
  padding: 8px 10px;
  margin: 8px 0;
  line-height: 1.5;
`;

export const InlineError = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.error};
  background: ${({ theme }) => `${theme.colors.error}12`};
  border: 1px solid ${({ theme }) => `${theme.colors.error}33`};
  border-radius: 8px;
  padding: 8px 10px;
  margin: 8px 0;
  line-height: 1.5;
`;

export const ConfirmBox = styled.div`
  margin-top: 12px;
  border: 1px solid ${({ theme }) => `${theme.colors.error}35`};
  background: ${({ theme }) => `${theme.colors.error}0e`};
  border-radius: 10px;
  padding: 12px;
`;

export const ConfirmTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 6px;
`;

export const ConfirmDesc = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  margin-bottom: 10px;
`;

export const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

/* ---- Usage bars ---- */

export const UsageBlock = styled.div`
  margin-bottom: 16px;
  &:last-child { margin-bottom: 0; }
`;

export const UsageLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 6px;
`;

export const UsageNumbers = styled.span<{ $warn: boolean }>`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${({ $warn, theme }) => ($warn ? theme.colors.error : theme.colors.textMuted)};
`;

export const UsageBarTrack = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  overflow: hidden;
`;

export const UsageBarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

/* ---- Billing empty ---- */

export const EmptyTabWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 12px;
`;

export const EmptyIcon = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

export const EmptyTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const EmptyDesc = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Service settings ---- */

export const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
`;

export const SettingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SettingName = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const SettingDesc = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const LinkItem = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;
